import { useEffect, useRef } from 'preact/hooks'
import { assetUrl } from '../runnerPreview/previewLayout'
import ov from './overlays.module.css'
import styles from './items.module.css'
import { Icon } from './icons'
import { FAMILY_TEXT, ITEM_INFO } from './itemText'
import type { ItemInfo } from './itemText'

/** The open item card; Escape (handled by the hub) or the close button shuts it. */
function ItemCard({ item, onClose }: { item: ItemInfo; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null)
  useEffect(() => close.current?.focus(), [item])
  return (
    <div class={`${ov.card} ${ov.itemCard}`} role="dialog" aria-label={item.name}>
      <img class={ov.itemCardIcon} src={assetUrl(item.icon)} alt="" />
      <div class={ov.itemCardText}>
        <strong>{item.name}</strong>
        <span class={`${ov.family} ${ov[item.family]}`}>{FAMILY_TEXT[item.family]}</span>
        <p>{item.effect}</p>
        <p class={ov.note}>Grab item boxes in the race to get one.</p>
      </div>
      <button ref={close} type="button" class={ov.closeButton} aria-label="Close" onClick={onClose}>
        <Icon name="close" />
      </button>
    </div>
  )
}

/**
 * Items row (PHASE3 T3.1): the race items in round slots with a "?" badge; a tap opens a card,
 * a press anywhere outside the row closes it. No counts: items come from boxes in the race, not
 * from owned stacks (D-048).
 */
export function ItemsRow({
  open,
  onOpen,
}: {
  open: string | null
  onOpen: (id: string | null) => void
}) {
  const item = ITEM_INFO.find((i) => i.id === open) ?? null
  const slots = useRef(new Map<string, HTMLButtonElement>())
  const last = useRef<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open === null) return
    const onDown = (e: Event): void => {
      const target = e.target as Node | null
      if (!box.current || !target || box.current.contains(target)) return
      last.current = null // Closed from elsewhere: focus stays where the press went.
      onOpen(null)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open, onOpen])
  useEffect(() => {
    // Card closed (button, Escape or a second tap): focus goes back to its slot.
    if (open === null && last.current) slots.current.get(last.current)?.focus()
    last.current = open
  }, [open])
  return (
    <div ref={box} class={styles.items}>
      {item && <ItemCard item={item} onClose={() => onOpen(null)} />}
      <div class={styles.itemRow} role="group" aria-label="Race items">
        {ITEM_INFO.map((i) => (
          <button
            key={i.id}
            ref={(el) => {
              if (el) slots.current.set(i.id, el)
            }}
            type="button"
            class={`${styles.itemSlot} ${open === i.id ? styles.itemOn : ''}`}
            aria-label={`${i.name}: what it does`}
            aria-expanded={open === i.id}
            onClick={() => onOpen(open === i.id ? null : i.id)}
          >
            <img src={assetUrl(i.icon)} alt="" draggable={false} />
            <span class={styles.itemBadge} aria-hidden="true">
              ?
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
